// Shared formatters: money always reads the same way across the portal.

export const cx = (...parts) => parts.flat().filter(Boolean).join(" ");

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[₹,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** ₹1,25,000 (Indian grouping). `sign` adds +/− for money in/out columns. */
export const formatINR = (value, { decimals = 0, sign = false, fallback = "—" } = {}) => {
  const n = toNumber(value);
  if (n === null) return fallback;
  const abs = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const prefix = n < 0 ? "−" : sign && n > 0 ? "+" : "";
  return `${prefix}₹${abs}`;
};

/** ₹12.5L / ₹1.2Cr / ₹45k for axes, KPIs and chart labels. */
export const formatINRCompact = (value, { fallback = "—" } = {}) => {
  const n = toNumber(value);
  if (n === null) return fallback;
  const abs = Math.abs(n);
  const neg = n < 0 ? "−" : "";
  const trim = (x) => x.toFixed(1).replace(/\.0$/, "");
  if (abs >= 1e7) return `${neg}₹${trim(abs / 1e7)}Cr`;
  if (abs >= 1e5) return `${neg}₹${trim(abs / 1e5)}L`;
  if (abs >= 1e3) return `${neg}₹${Math.round(abs / 1e3)}k`;
  return `${neg}₹${Math.round(abs)}`;
};

export const formatNumber = (value, { fallback = "—" } = {}) => {
  const n = toNumber(value);
  return n === null ? fallback : n.toLocaleString("en-IN");
};

export const formatPercent = (value, { decimals = 1, sign = false, fallback = "—" } = {}) => {
  const n = toNumber(value);
  if (n === null) return fallback;
  const s = `${Math.abs(n).toFixed(decimals).replace(/\.0+$/, "")}%`;
  return n < 0 ? `−${s}` : sign && n > 0 ? `+${s}` : s;
};

export const formatDate = (value, { withTime = false, fallback = "—" } = {}) => {
  if (!value) return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });
};

/** Percent change between two numbers, or null when it can't be computed. */
export const percentChange = (current, previous) => {
  const c = toNumber(current), p = toNumber(previous);
  if (c === null || p === null || p === 0) return null;
  return ((c - p) / Math.abs(p)) * 100;
};
